import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { IoClose } from 'react-icons/io5';

interface LevelUpAnimationProps {
  show: boolean;
  level: number;
  levelName: string;
  badge: string;
  onComplete: () => void;
}

export const LevelUpAnimation: React.FC<LevelUpAnimationProps> = ({
  show,
  level,
  levelName,
  badge,
  onComplete,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [animationPhase, setAnimationPhase] = useState<'enter' | 'show'>('enter');

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      setAnimationPhase('enter');
      
      // Enter phase - slide in from top
      setTimeout(() => setAnimationPhase('show'), 100);
    }
  }, [show]);

  const handleClose = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsVisible(false);
    onComplete();
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    // Only close if clicking the background, not the modal content
    if (e.target === e.currentTarget) {
      handleClose(e);
    }
  };

  const handleModalClick = (e: React.MouseEvent) => {
    // Prevent background click when clicking inside modal
    e.stopPropagation();
  };

  if (!isVisible) return null;

  const getAnimationClasses = () => {
    switch (animationPhase) {
      case 'enter':
        return 'opacity-0 -translate-y-full scale-75';
      case 'show':
        return 'opacity-100 translate-y-0 scale-100';
      default:
        return 'opacity-0';
    }
  };

  const getLevelGradient = (level: number) => {
    const gradients = {
      1: 'from-green-400 to-green-600',
      2: 'from-blue-400 to-blue-600',
      3: 'from-yellow-400 to-yellow-600',
      4: 'from-orange-400 to-orange-600',
      5: 'from-red-400 to-red-600',
    };
    return gradients[level as keyof typeof gradients] || 'from-primary to-primary/80';
  };

  const animation = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      onClick={handleBackgroundClick}
    >
      {/* Background Overlay */}
      <div 
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-all duration-1000 ${
          animationPhase === 'show' ? 'opacity-100' : 'opacity-0'
        }`}
      />
      
      {/* Main Animation Container */}
      <div 
        className={`relative transition-all duration-1000 ease-out ${getAnimationClasses()}`}
        onClick={handleModalClick}
      >
        {/* Glow Effect */}
        <div className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${getLevelGradient(level)} opacity-30 blur-xl scale-110`} />
        
        {/* Main Card */}
        <div className="relative bg-gradient-to-br from-gray-800/95 to-gray-900/95 backdrop-blur-md rounded-3xl border-2 border-gradient-to-r border-primary/30 p-8 min-w-[400px] max-w-md mx-4">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-gray-700/50 hover:bg-gray-600/50 transition-colors duration-200 text-gray-300 hover:text-white z-10"
            aria-label="Close level up notification"
            type="button"
          >
            <IoClose className="text-xl" />
          </button>

          {/* Sparkle Effects */}
          <div className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className={`absolute w-2 h-2 bg-primary rounded-full animate-bounce`}
                style={{
                  left: `${20 + (i * 10)}%`,
                  top: `${15 + (i % 3) * 20}%`,
                  animationDelay: `${i * 200}ms`,
                  animationDuration: `${1000 + (i * 100)}ms`,
                }}
              />
            ))}
          </div>
          
          {/* Content */}
          <div className="relative text-center space-y-6 pt-4">
            {/* Level Up Text */}
            <div className="space-y-2">
              <h2 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent animate-pulse">
                LEVEL UP!
              </h2>
              <div className="w-16 h-1 bg-gradient-to-r from-primary to-primary/80 mx-auto rounded-full" />
            </div>
            
            {/* Badge and Level */}
            <div className="space-y-4">
              <div className={`text-8xl animate-bounce transition-transform duration-300 ${
                animationPhase === 'show' ? 'scale-110' : 'scale-100'
              }`}>
                {badge}
              </div>
              
              <div className="space-y-2">
                <div className="text-6xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  {level}
                </div>
                <h3 className="text-2xl font-bold text-white">
                  {levelName}
                </h3>
              </div>
            </div>
            
            {/* Congratulations */}
            <div className="space-y-2">
              <p className="text-lg text-gray-300">
                Congratulations! You've reached
              </p>
              <p className="text-xl font-semibold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                Level {level}: {levelName}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 pt-4">
              <button
                onClick={handleClose}
                className="px-8 py-3 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 rounded-xl text-gray-900 font-semibold transition-all duration-300 transform hover:scale-105 z-10"
                type="button"
              >
                Awesome!
              </button>
              <p className="text-xs text-gray-400">
                Click anywhere outside to close
              </p>
            </div>
            
            {/* Animated Border */}
            <div className="absolute inset-0 rounded-3xl border-2 border-transparent bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20 bg-clip-border animate-pulse pointer-events-none" />
          </div>
        </div>
        
        {/* Particle Effects */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(12)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-primary/80 rounded-full animate-ping"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2000}ms`,
                animationDuration: `${1500 + Math.random() * 1000}ms`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );

  // Render using portal to ensure it appears on top of everything
  return createPortal(animation, document.body);
}; 